import React from 'react';
import { FiChevronRight, FiRefreshCw, FiCheck, FiX } from 'react-icons/fi';
import { AiOutlineDelete } from 'react-icons/ai';
import { TooltipTop } from '@components/tooltips';
import { AirflowDagRun } from '../types';
import { DagRunStateBadge, formatDagVersion, formatDateTime, formatDurationSeconds, RunTypeLabel } from '../utils';
import styles from '../styles.module.scss';

type RunHeaderProps = {
    dagId: string;
    runId: string;
    run: AirflowDagRun | null;
    isLoading: boolean;
    onDagClick: () => void;
    onClearRun: () => void;
    onMarkSuccess: () => void;
    onMarkFailed: () => void;
    onDeleteRun: () => void;
};

/*
 * DAG-run detail header: breadcrumb, state + actions, and a metadata grid
 */
export function RunHeader({ dagId, runId, run, isLoading, onDagClick, onClearRun, onMarkSuccess, onMarkFailed, onDeleteRun }: RunHeaderProps) {
    return (
        <div>
            <div className={styles.breadcrumb}>
                <span className={styles.breadcrumbLink} onClick={onDagClick}>
                    {dagId}
                </span>
                <FiChevronRight />
                <span style={{ fontFamily: 'monospace' }}>{runId}</span>
            </div>
            {isLoading || !run ? (
                <div className={styles.headerRow}>
                    <h1>{runId}</h1>
                </div>
            ) : (
                <>
                    <div className={styles.headerRow}>
                        <h1>
                            <span style={{ fontFamily: 'monospace' }}>{run.dag_run_id}</span>
                            <DagRunStateBadge state={run.state} />
                        </h1>
                        <div style={{ display: 'flex', gap: 16 }}>
                            <TooltipTop title="Clear DAG Run">
                                <span className={styles.actionIcon} onClick={onClearRun}>
                                    <FiRefreshCw />
                                </span>
                            </TooltipTop>
                            <TooltipTop title="Mark Success">
                                <span className={styles.actionIcon} onClick={onMarkSuccess}>
                                    <FiCheck />
                                </span>
                            </TooltipTop>
                            <TooltipTop title="Mark Failed">
                                <span className={styles.actionIcon} onClick={onMarkFailed}>
                                    <FiX />
                                </span>
                            </TooltipTop>
                            <TooltipTop title="Delete">
                                <span className={`${styles.actionIcon} ${styles.destructive}`} onClick={onDeleteRun}>
                                    <AiOutlineDelete />
                                </span>
                            </TooltipTop>
                        </div>
                    </div>
                    <div className={styles.metaGrid}>
                        <div className={styles.metaField}>
                            <div className={styles.metaLabel}>Logical Date</div>
                            <div className={styles.metaValue}>{formatDateTime(run.execution_date ?? run.logical_date)}</div>
                        </div>
                        <div className={styles.metaField}>
                            <div className={styles.metaLabel}>Run Type</div>
                            <div className={styles.metaValue}>
                                <RunTypeLabel runType={run.run_type} />
                            </div>
                        </div>
                        <div className={styles.metaField}>
                            <div className={styles.metaLabel}>Start Date</div>
                            <div className={styles.metaValue}>{formatDateTime(run.start_date)}</div>
                        </div>
                        <div className={styles.metaField}>
                            <div className={styles.metaLabel}>End Date</div>
                            <div className={styles.metaValue}>{formatDateTime(run.end_date)}</div>
                        </div>
                        <div className={styles.metaField}>
                            <div className={styles.metaLabel}>Duration</div>
                            <div className={styles.metaValue}>{formatDurationSeconds(run.duration)}</div>
                        </div>
                        <div className={styles.metaField}>
                            <div className={styles.metaLabel}>Triggered By</div>
                            <div className={styles.metaValue}>{run.triggering_user_name ?? run.triggered_by ?? '—'}</div>
                        </div>
                        <div className={styles.metaField}>
                            <div className={styles.metaLabel}>DAG Version</div>
                            <div className={styles.metaValue}>{formatDagVersion(run.dag_versions)}</div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
